import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient, queryClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceEventsQueryKey } from '@/utils/queryKeys';
import { type ExperienceCategory, useCategoryCache } from './useCategoryCache';
import type {
  DeleteExperienceCategoryMutation,
  DeleteExperienceCategoryMutationVariables,
} from './useDeleteCategory.generated';

const query = gql`
  mutation DeleteExperienceCategory($input: DeleteExperienceCategoryInput!) {
    deleteExperienceCategory(input: $input)
  }
`;

export const useDeleteCategory = () => {
  const experience = useEffectiveExperience();
  const cache = useCategoryCache();

  const { mutate, isPending } = useMutation<
    DeleteExperienceCategoryMutation,
    QueryError,
    DeleteExperienceCategoryMutationVariables,
    ExperienceCategory | undefined
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        DeleteExperienceCategoryMutation,
        DeleteExperienceCategoryMutationVariables
      >(query, variables),
    onMutate: ({ input }) => {
      const category = cache.getCategory(input.categoryId);
      cache.removeCategory(input.categoryId);
      return category;
    },
    onSuccess: () => {
      // Deleting a category clears it from any events that referenced it.
      queryClient.invalidateQueries({
        queryKey: createExperienceEventsQueryKey(experience.id),
      });
    },
    onError: (_error, _variables, category) => {
      if (category) {
        cache.addCategory(category.id, category);
      }
    },
  });

  const removeCategory = useCallback(
    (
      categoryId: string,
      options?: MutateOptions<
        DeleteExperienceCategoryMutation,
        QueryError,
        DeleteExperienceCategoryMutationVariables,
        ExperienceCategory | undefined
      >
    ) =>
      mutate(
        {
          input: {
            experienceId: experience.id,
            categoryId,
          },
        },
        options
      ),
    [mutate, experience.id]
  );

  return { removeCategory, isPending };
};
